/**
 * PuppetListItem Component (Task 5.3)
 *
 * Individual puppet item in the puppet list.
 * Displays puppet name (bold), model path (muted), tool count.
 * Shows active indicator for currently active puppet.
 *
 * Features:
 * - Show puppet name (bold), model path (muted), tool count
 * - Click to select puppet for editing
 * - Show active indicator for currently active puppet
 * - Delete button (disabled when cannot delete)
 */

import { Trash2, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import type { Puppet } from '@/stores/puppetStore';

interface PuppetListItemProps {
  /**
   * The puppet to display
   */
  puppet: Puppet;

  /**
   * Whether this puppet is currently selected for editing
   */
  isSelected: boolean;

  /**
   * Whether this puppet is the active puppet in the main UI
   */
  isActive: boolean;

  /**
   * Whether this puppet can be deleted (false if it's the last puppet)
   */
  canDelete: boolean;

  /**
   * Callback when puppet is clicked to select
   */
  onSelect: () => void;

  /**
   * Callback when delete button is clicked
   */
  onDelete: () => void;
}

export function PuppetListItem({
  puppet,
  isSelected,
  isActive,
  canDelete,
  onSelect,
  onDelete,
}: PuppetListItemProps) {
  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (canDelete) {
      onDelete();
    }
  };

  const toolCount = puppet.tools.length;
  const toolCountLabel = toolCount === 1 ? '1 tool' : `${toolCount} tools`;

  return (
    <motion.div
      data-testid={`puppet-list-item-${puppet.id}`}
      data-selected={isSelected}
      role="option"
      aria-selected={isSelected}
      onClick={onSelect}
      className="group relative cursor-pointer rounded-md p-3 transition-colors"
      style={{
        backgroundColor: isSelected
          ? 'var(--color-dark-border)'
          : 'transparent',
      }}
      whileHover={{
        backgroundColor: isSelected
          ? 'var(--color-dark-border)'
          : 'rgba(255, 255, 255, 0.05)',
      }}
      whileTap={{ scale: 0.99 }}
    >
      {/* Active indicator */}
      {isActive && (
        <div
          className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r"
          style={{ backgroundColor: 'var(--color-body-head)' }}
          title="Active puppet"
        />
      )}

      {/* Main content area */}
      <div className="flex items-start justify-between gap-2 pl-2">
        <div className="min-w-0 flex-1">
          {/* Puppet name with active badge */}
          <div className="flex items-center gap-2">
            <span
              className="truncate text-sm font-semibold"
              style={{ color: 'var(--color-dark-text)' }}
            >
              {puppet.name}
            </span>
            {isActive && (
              <span
                className="flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs"
                style={{
                  backgroundColor: 'var(--color-body-head)',
                  color: 'white',
                }}
              >
                <Check className="h-3 w-3" />
                <span className="sr-only">Active</span>
              </span>
            )}
          </div>

          {/* Model path */}
          {puppet.modelPath && (
            <div
              className="mt-0.5 truncate text-xs"
              style={{ color: 'var(--color-dark-text-muted)' }}
              title={puppet.modelPath}
            >
              {puppet.modelPath || 'No model'}
            </div>
          )}

          {/* Tool count */}
          <div
            className="mt-1 text-xs"
            style={{ color: 'var(--color-dark-text-muted)' }}
          >
            {toolCountLabel}
          </div>
        </div>

        {/* Delete button */}
        <motion.button
          onClick={handleDeleteClick}
          disabled={!canDelete}
          className="shrink-0 rounded p-1.5 opacity-0 transition-opacity group-hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-30"
          style={{
            color: canDelete
              ? 'var(--color-body-control)'
              : 'var(--color-dark-text-muted)',
          }}
          whileHover={canDelete ? { scale: 1.1 } : undefined}
          whileTap={canDelete ? { scale: 0.9 } : undefined}
          aria-label={`Delete ${puppet.name}`}
          title={
            canDelete
              ? `Delete ${puppet.name}`
              : 'Cannot delete the last puppet'
          }
        >
          <Trash2 className="h-4 w-4" />
        </motion.button>
      </div>
    </motion.div>
  );
}
